import { IconPlusCircle } from "@/assets/icons/IconPlusCircle";
import { background, button, divider, green, text } from "@/theme/colors";
import { rounded, spacing } from "@/theme/variables";
import { isNumber } from "@/utils/number";
import { Box, CircularProgress, LinearProgress, Typography, styled } from "@mui/material";
import { MRT_RowData, MRT_TableInstance, MaterialReactTable } from "material-react-table";

export type TTableExtendOptions = { addNewRow?: () => void };

type TTableProps<T extends MRT_RowData> = {
  addBtnText?: string;
  table: MRT_TableInstance<T> & TTableExtendOptions;
  isLoading?: boolean;
  isFetching?: boolean;
  maxHeight?: number | string;
  headRowHeight?: number | string;
  bodyRowHeight?: number | string;
  borderColor?: string;
  addNewRow?: () => void;
  id?: string;
  hiddenAddBtn?: boolean;
  height?: number | string;
  minHeight?: number | string;
};

export const Table = <T extends MRT_RowData>({
  addBtnText,
  table,
  isLoading,
  isFetching,
  maxHeight,
  borderColor = divider.middle,
  headRowHeight = 40,
  bodyRowHeight = 42,
  id,
  hiddenAddBtn,
  height,
  minHeight,
}: TTableProps<T>) => {
  const { addNewRow } = table;
  const hasFnAddRow = !!addNewRow;
  return (
    <StyledTable
      id={id}
      tableMaxHeight={maxHeight}
      height={height}
      minHeight={minHeight}
      headRowHeight={headRowHeight}
      bodyRowHeight={bodyRowHeight}
      borderColor={borderColor}
      pb={hasFnAddRow && !hiddenAddBtn ? 4.5 : 0}
    >
      <MaterialReactTable table={table} />
      {table.getRowCount() === 0 && (
        <CenterBox>
          <Typography color={text.tertiary} fontWeight={700}>
            データがありません。
          </Typography>
        </CenterBox>
      )}
      {isLoading && (
        <CenterBox>
          <CircularProgress sx={{ color: text.tertiary }} size="24px" />
        </CenterBox>
      )}
      {isFetching && !isLoading && <LinearProgress sx={{ height: 3 }} color="inherit" />}
      {hasFnAddRow && !hiddenAddBtn && (
        <AddRowButton onClick={addNewRow}>
          <IconPlusCircle color={background.main} />
          <Typography variant="cap12" padding="0px !important">
            {addBtnText ?? "行を追加する"}
          </Typography>
        </AddRowButton>
      )}
    </StyledTable>
  );
};

const options = {
  shouldForwardProp: (propName: string) => !["tableMaxHeight", "headRowHeight", "bodyRowHeight", "borderColor"].includes(propName),
};

const StyledTable = styled(Box, options)<{
  tableMaxHeight?: number | string;
  headRowHeight?: number | string;
  bodyRowHeight?: number | string;
  borderColor?: string;
}>`
  position: relative;
  border-radius: ${rounded.sm};
  overflow: hidden;
  border: 1px solid ${({ borderColor }) => borderColor};
  * {
    box-shadow: none;
  }
  .MuiTableContainer-root {
    max-height: ${({ tableMaxHeight }) => `${tableMaxHeight ? (isNumber(tableMaxHeight) ? `${tableMaxHeight}px` : tableMaxHeight) : "unset"}`};
    height: ${({ height }) => `${height ? (isNumber(height) ? `${height}px` : height) : "unset"}`};
    min-height: ${({ minHeight }) => `${minHeight ? (isNumber(minHeight) ? `${minHeight}px` : minHeight) : "unset"}`};
  }
  .MuiToolbar-dense {
    display: none;
  }
  .MuiTableCell-head {
    background-color: ${background.primary} !important;
    padding-top: 8px !important;
    padding-bottom: 8px !important;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
    text-align: left;
  }
  .Mui-TableHeadCell-Content-Labels {
    > span {
      margin-left: 4px;
      img {
        opacity: 0.2;
      }
      .MuiBadge-badge {
        display: none;
      }
    }
  }
  .MuiTableCell-body {
    background-color: ${background.white} !important;
    font-size: 14px;
    font-weight: 400;
    line-height: 28px;
  }

  .MuiTableCell-root:has(.MuiCheckbox-root) {
    max-width: 56px;
    min-width: 56px;
  }

  // Default
  .MuiTableRow-root {
    box-shadow: none;
    .MuiTableCell-root {
      padding: ${spacing.xxs} ${spacing.sm};
      border-color: ${divider.middle};
      white-space: nowrap;
      opacity: 1;

      & .MuiCheckbox-root {
        margin-left: 8px;
      }
    }
    &:not(.MuiTableRow-head):last-child {
      .MuiTableCell-root {
        border-bottom: 0;
      }
    }
  }
  .MuiTableHead-root {
    .MuiTableCell-root {
      height: ${({ headRowHeight }) => `${headRowHeight ? (isNumber(headRowHeight) ? `${headRowHeight}px` : headRowHeight) : "unset"}`};
    }
    .Mui-TableHeadCell-Content {
      height: 100%;
    }
  }
  .MuiTableBody-root {
    .MuiTableCell-root {
      height: ${({ bodyRowHeight }) => `${bodyRowHeight ? (isNumber(bodyRowHeight) ? `${bodyRowHeight}px` : bodyRowHeight) : "unset"}`};
    }
  }
  // Hover
  .MuiTableRow-root:not(.MuiTableRow-head):hover {
    &,
    td {
      background-color: ${background.white};
      &::after {
        background-color: ${background.primary};
      }
    }
  }
  // Click/Pressed
  .Mui-selected {
    background-color: ${background.white} !important;
    color: ${button.primary} !important;
    td::after {
      background: ${background.secondary}!important;
    }
  }

  .MuiPaper-root {
    box-shadow: none;
  }
  .MuiPaper-root > .MuiBox-root {
    display: none;
  }

  .MuiCheckbox-root {
    color: ${button.primary};
    height: 24px;
    width: 24px;
    padding: 0;
  }

  .Mui-disabled {
    color: ${button.disable};
  }

  .MuiTableCell-root {
    padding: 0 8px !important;
    //  background-color: ${background.white} !important;
  }

  // Disabled Row
  .MuiTableCell-root.disabled {
    opacity: 1;
    background: ${background.disable}!important;
    pointer-events: none;
  }

  // added row
  .MuiTableCell-root.added {
    background: ${green[99]}!important;
    &::after {
      background: unset !important;
    }
  }

  // Failed row
  .MuiTableCell-root.failed {
    background: ${background.error}!important;
    &::after {
      background: unset !important;
    }
  }

  // Grayed-out row (e.g. out-of-scope)
  .MuiTableCell-root.gray {
    background: ${background.disable}!important;
    &::after {
      background: unset !important;
    }
  }

  // Failed row
  .MuiTableCell-root.clickable {
    cursor: pointer;
  }

  .MuiTypography-root {
    padding: 8px;
  }
`;

const CenterBox = styled(Box)`
  position: absolute;
  margin-top: 17px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  background-color: ${background.white};
  text-align: center;
  padding: 20px;
`;

const AddRowButton = styled(Box)`
  display: flex;
  align-items: center;
  gap: 4px;
  position: absolute;
  margin-top: 8px;
  margin-left: 16px;
  cursor: pointer;
`;
